import React, { useEffect, useState } from 'react';
import {
  faChalkboardUser,
  faChildren,
  faCircleCheck,
  faDiagramProject,
  faHouseUser,
  faSchool,
  faUserCheck,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { AdminNavHandler } from '../../adminNav';
import { AudienceDashboard, fetchAudienceDashboard } from '../services/AdminStats.service';
import SignupsChart from './SignupsChart';
import { Section, Tile } from './Tile';

const pctOf = (num: number, den: number): string => (den > 0 ? `${Math.round((num / den) * 1000) / 10}% ` : '— ');

/**
 * Registrations + classroom usage. Self-contained (owns its own fetch) so the fulfillment dashboard
 * stays independent of it — a slow audience query never blocks the order numbers.
 *
 * Students are not a registration: they are provisioned in bulk by a teacher, so they are reported
 * under Classrooms, never as a signup.
 */
const AudienceStats: React.FC<{ onOptionClick?: AdminNavHandler; refreshKey?: number }> = ({
  onOptionClick,
  refreshKey,
}) => {
  const [audience, setAudience] = useState<AudienceDashboard | null>(null);

  useEffect(() => {
    let stale = false;
    fetchAudienceDashboard().then((data) => {
      if (!stale && data) setAudience(data);
    });
    return () => {
      stale = true;
    };
  }, [refreshKey]);

  if (!audience) return null;

  const { registrations: reg, classrooms: cls } = audience;
  const accounts = reg.teachers + reg.parents;

  const open = (option: string) => onOptionClick?.(option);

  return (
    <>
      <Section title="Registrations">
        <Tile
          label="Teachers"
          value={reg.teachers}
          icon={faChalkboardUser}
          tone="blue"
          sub={`+${reg.teachers30d} in 30d`}
          onClick={() => open('Customers')}
          hint="Open in Users"
        />
        <Tile
          label="Parents"
          value={reg.parents}
          icon={faHouseUser}
          tone="blue"
          sub={`+${reg.parents30d} in 30d`}
          onClick={() => open('Customers')}
          hint="Open in Users"
        />
        <Tile
          label="Active (30d)"
          value={reg.active30d}
          icon={faUsers}
          tone="green"
          sub={`${pctOf(reg.active30d, accounts)}of accounts`}
        />
        <Tile
          label="Teacher onboarding"
          value={reg.onboardedTeachers}
          icon={faUserCheck}
          tone="green"
          sub={`${pctOf(reg.onboardedTeachers, reg.teachers)}complete`}
        />
      </Section>

      <SignupsChart series={audience.signupSeries} truncated={audience.signupSeriesTruncated} />

      <Section title="Classrooms">
        <Tile
          label="Classrooms"
          value={cls.total}
          icon={faSchool}
          tone="blue"
          sub={`+${cls.new30d} in 30d`}
          onClick={() => open('Classrooms')}
          hint="Open in Classrooms"
        />
        <Tile
          label="Students"
          value={cls.students}
          icon={faChildren}
          tone="blue"
          sub={cls.avgStudentsPerClassroom != null ? `${cls.avgStudentsPerClassroom} per classroom` : undefined}
          onClick={() => open('Students')}
          hint="Open in Students"
        />
        <Tile
          label="Active projects"
          value={cls.projectsActive}
          icon={faDiagramProject}
          tone="amber"
          sub={`of ${cls.projects} total`}
        />
        <Tile
          label="Projects completed"
          value={cls.projectsCompleted}
          icon={faCircleCheck}
          tone="green"
          sub={`${pctOf(cls.projectsCompleted, cls.projects)}of projects`}
        />
      </Section>
    </>
  );
};

export default AudienceStats;
